import { useCallback, useEffect, useState } from "react";
import Navbar from "../Navbar";
import {
  adminTreatReports,
  adminDismissTreatReport,
  adminBlockTreatSender,
} from "../../services/treatApi";
import "../Pro/Pro.css";
import "./Treat.css";

// =====================================================
// ADMIN — REPORTED TREAT GREETINGS
//
// Behind ProtectedAdminRoute here and adminOnly on the server. "Block" sets
// the account's existing `blocked` flag, which the server enforces at login
// and on every authenticated request.
// =====================================================

const TABS = [
  { key: "open", label: "Open" },
  { key: "actioned", label: "Blocked" },
  { key: "dismissed", label: "Dismissed" },
  { key: "", label: "All" },
];

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN");
};

// `embedded` renders just the panel, for the "Reported Greetings" tab of the
// Buy Me a Treat admin page.
function AdminTreatReports({ embedded = false }) {
  const [tab, setTab] = useState("open");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await adminTreatReports(tab));
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const act = async (report, kind) => {
    if (kind === "block") {
      const ok = window.confirm(
        `Block ${report.reportedUser.name} from TheBlogsSphere? They will not be able to log in or use the app.`
      );
      if (!ok) return;
    }
    const note = window.prompt("Admin note (optional):", "") ?? "";
    setBusyId(report._id);
    try {
      const res =
        kind === "block"
          ? await adminBlockTreatSender(report._id, note)
          : await adminDismissTreatReport(report._id, note);
      setToast({ type: "success", msg: res?.message || "Done." });
      load();
    } catch (err) {
      setToast({ type: "error", msg: err?.response?.data?.message || "Action failed" });
    } finally {
      setBusyId(null);
    }
  };

  const panel = (
    <>
        {embedded && data && (
          <p className="treat-dim">
            {data.counts.open} open · {data.counts.actioned} blocked · {data.counts.dismissed} dismissed
          </p>
        )}
        <div className="pro-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key || "all"}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`pro-tab${tab === t.key ? " pro-tab-active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="pro-loading-screen">
            <div className="pro-spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button type="button" className="pro-btn pro-btn-primary" onClick={load}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && data && (
          data.items.length === 0 ? (
            <div className="pro-card pro-empty">
              <p>No reports here.</p>
            </div>
          ) : (
            <div className="pro-table-scroll">
              <table className="pro-table">
                <thead>
                  <tr>
                    <th>Reported message</th>
                    <th>Reader (sender)</th>
                    <th>Reported by</th>
                    <th>Reason</th>
                    <th>Reported on</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r._id}>
                      <td className="treat-cell-message">
                        <span>“{r.message}”</span>
                        {r.blogTitle && <small className="pro-table-sub">On: {r.blogTitle}</small>}
                      </td>
                      <td>
                        <span className="pro-table-strong">{r.reportedUser.name}</span>
                        <small className="pro-table-sub">{r.reportedUser.email}</small>
                        {r.reportedUser.blocked && (
                          <span className="pro-status-pill pro-status-failed">Blocked</span>
                        )}
                      </td>
                      <td>
                        <span className="pro-table-strong">{r.reporter.name}</span>
                        <small className="pro-table-sub">{r.reporter.email}</small>
                      </td>
                      <td>
                        {r.reason}
                        {r.details && <small className="pro-table-sub">{r.details}</small>}
                      </td>
                      <td>{formatDateTime(r.reportedAt)}</td>
                      <td>
                        <span
                          className={`pro-status-pill pro-status-${
                            r.status === "open" ? "pending" : r.status === "actioned" ? "failed" : "expired"
                          }`}
                        >
                          {r.status === "actioned" ? "Blocked" : r.status}
                        </span>
                        {r.adminNote && <small className="pro-table-sub">{r.adminNote}</small>}
                      </td>
                      <td>
                        {r.status === "open" ? (
                          <div className="treat-admin-actions">
                            <button
                              type="button"
                              className="pro-btn pro-btn-sm treat-btn-danger"
                              disabled={busyId === r._id || r.reportedUser.blocked}
                              onClick={() => act(r, "block")}
                            >
                              Block user
                            </button>
                            <button
                              type="button"
                              className="pro-btn pro-btn-sm pro-btn-ghost"
                              disabled={busyId === r._id}
                              onClick={() => act(r, "dismiss")}
                            >
                              Dismiss
                            </button>
                          </div>
                        ) : (
                          <small className="pro-table-sub">{formatDateTime(r.reviewedAt)}</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
    </>
  );

  if (embedded) {
    return (
      <>
        {toast && <div className={`pro-toast pro-toast-${toast.type}`}>{toast.msg}</div>}
        {panel}
      </>
    );
  }

  return (
    <div className="pro-page-wrap">
      <Navbar />
      {toast && <div className={`pro-toast pro-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="pro-container">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">⚑ TREAT GREETING REPORTS</div>
          <h1 className="pro-hero-title">Reported reader greetings</h1>
          {data && (
            <p className="pro-hero-sub">
              {data.counts.open} open · {data.counts.actioned} blocked · {data.counts.dismissed} dismissed
            </p>
          )}
        </header>
        {panel}
      </div>
    </div>
  );
}

export default AdminTreatReports;
